import React, { useState } from "react";
import Box from "@mui/material/Box";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Calendar } from "react-multi-date-picker";
import DatePanel from "react-multi-date-picker/plugins/date_panel";
import { useSnackbar } from "notistack";
import { get_shoping_list_api } from "src/DAL/Profile/Profile";
import { _get_user_from_localStorage_new } from "src/DAL/localStorage/localStorage";
import { getValidMealArray } from "src/utils/constants";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { logo_main } from "src/assets";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrPage from "src/design-system/components/GrPage";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrIcon from "src/design-system/components/GrIcon";
import GrCard from "src/design-system/components/GrCard";
import GrCardHeader from "src/design-system/components/GrCardHeader";
import GrButton from "src/design-system/components/GrButton";
import GrText from "src/design-system/components/GrText";
import GrNotice from "src/design-system/components/GrNotice";
import GrListRow from "src/design-system/components/GrListRow";
import GrBadge from "src/design-system/components/GrBadge";
import GrEmptyState from "src/design-system/components/GrEmptyState";

/**
 * Shopping List (/dashboard/shopping-list, from the dashboard): pick days on the calendar, create
 * the list of foods from those days' meals, download it as a PDF.
 */

// Calendar and food list side by side on wider screens; one column on phones.
const layoutSx = {
  display: "grid",
  gap: gr.space[6],
  alignItems: "start",
  gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
};
const pickTopSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: gr.space[3],
  mb: gr.space[4],
};
const profileLinkSx = {
  color: gr.color.brandStrong,
  fontWeight: gr.font.weight.semibold,
  textDecoration: "underline",
};
// react-multi-date-picker in the card: full width, tokens instead of its fixed 500px / theme colours.
const calendarSx = {
  "& .rmdp-wrapper": {
    width: "100% !important",
    fontFamily: gr.font.family,
    fontSize: `${gr.font.size.md} !important`,
    boxShadow: "none",
    border: `1px solid ${gr.color.line}`,
    borderRadius: gr.radius.tile,
  },
  "& .rmdp-calendar": { width: "100%", padding: gr.space[2] },
  "& .rmdp-day-picker > div": { width: "100%" },
  "& .rmdp-week": { width: "100% !important", justifyContent: "space-around" },
  "& .rmdp-header-values": {
    color: gr.color.ink,
    fontWeight: gr.font.weight.semibold,
  },
  "& .rmdp-week-day": {
    color: `${gr.color.muted} !important`,
    fontSize: gr.font.size.xs,
    fontWeight: gr.font.weight.medium,
  },
  "& .rmdp-arrow": { borderColor: `${gr.color.brandStrong} !important` },
  "& .rmdp-day span": { borderRadius: gr.radius.pill },
  "& .rmdp-day:not(.rmdp-disabled):not(.rmdp-day-hidden) span:hover": {
    background: `${gr.color.brandSoft} !important`,
    color: `${gr.color.brandDark} !important`,
  },
  "& .rmdp-day.rmdp-today span": {
    background: "transparent",
    color: gr.color.brandStrong,
    fontWeight: gr.font.weight.semibold,
  },
  "& .rmdp-day.rmdp-selected span:not(.highlight)": {
    background: `${gr.color.brandStrong} !important`,
    color: `${gr.color.onBrand} !important`,
    boxShadow: "none",
  },
  // Picked days under the calendar, as small chips.
  "& .rmdp-panel": { borderTop: `1px solid ${gr.color.line2}` },
  "& .rmdp-panel-header": { color: gr.color.ink2, fontWeight: gr.font.weight.medium },
  "& .rmdp-panel-body li": {
    background: `${gr.color.brandSoft} !important`,
    color: gr.color.brandDark,
    borderRadius: gr.radius.pill,
    boxShadow: "none",
  },
  "& .rmdp-panel-body li .b-date": { color: gr.color.brandDark, fontSize: gr.font.size.sm },
  "& .rmdp-panel-body li .b-deselect": { color: gr.color.brandDark, background: "transparent" },
};
const foodListSx = {
  m: 0,
  p: 0,
  maxHeight: "min(60vh, 520px)",
  overflowY: "auto",
  pr: gr.space[1],
};

const ShoppingList = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [dates, setDates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [listData, setListData] = useState([]);

  let user_info = _get_user_from_localStorage_new();

  const getTotalNutrient = (meals, nutrient, type) => {
    if (!meals && !Array.isArray(meals)) return "0.00";

    return meals
      .reduce((total, meal) => {
        if (!Array.isArray(meal[type])) return total;
        return (
          total +
          meal[type].reduce((mealTotal, food) => {
            const value = food?.measurement_units?.[nutrient] || 0;
            const quantity = parseFloat(food?.quantity) || 0;
            return mealTotal + value * (quantity / 100);
          }, 0)
        );
      }, 0)
      .toFixed(2);
  };

  const getTotalCalories = (meals, type) => {
    const totalCarbs = parseFloat(getTotalNutrient(meals, "carb", type));
    const totalProtein = parseFloat(getTotalNutrient(meals, "protein", type));
    const totalFat = parseFloat(getTotalNutrient(meals, "fat", type));

    return (totalCarbs * 4 + totalProtein * 4 + totalFat * 9).toFixed(2);
  };

  // Same rule as the meal popup: portions follow the member's current Regular Day calories.
  const getQuantity = (qun, mealsData) => {
    let quantity = 0;
    const mealCalories = parseFloat(getTotalCalories(mealsData, "meal_food_array"));
    if (user_info?.regular_day_calories && mealCalories > 0) {
      quantity = (user_info?.regular_day_calories / mealCalories) * qun;
    } else {
      return qun;
    }
    return quantity.toFixed(2);
  };

  const getQuantityPercentage = (qun) => {
    let quantity = 0;
    if (user_info?.deficit_value && user_info?.deficit_percentage) {
      let deficitPercentage = user_info?.deficit_value / 100;
      quantity = qun * (1 - deficitPercentage);
      return Number(quantity).toFixed(2);
    } else {
      return qun;
    }
  };
  function consolidateMealFoods(mealData) {
    if (!mealData || !Array.isArray(mealData)) {
      return [];
    }

    let foodMap = {};

    mealData.forEach((meal) => {
      if (!meal.meal_food_array || !Array.isArray(meal.meal_food_array)) return;

      meal.meal_food_array.forEach((item) => {
        let name = item.name;
        let quantity = parseFloat(item.quantity);

        if (foodMap[name]) {
          foodMap[name] += quantity;
        } else {
          foodMap[name] = quantity;
        }
      });
    });

    return Object.keys(foodMap).map((name) => ({
      name,
      quantity: foodMap[name].toFixed(2),
    }));
  }

  const get_shoping_list = async () => {
    setIsLoading(true);
    const postData = {
      dates: dates.map((item) => item.format("YYYY-MM-DD")),
    };
    const result = await get_shoping_list_api(postData);
    if (result.code === 200) {
      let meals_array = [];

      if (result.combined_workouts && result.combined_workouts.length > 0) {
        meals_array = result.combined_workouts.map((item) => {
          let mealData = getValidMealArray(item.meals);
          if (item.workout_source == "program") {
            return {
              meals: mealData.map((meal) => {
                return {
                  ...meal,
                  meal_food_array: meal?.meal_food_array
                    ?.map((unit) => {
                      if (unit.measurement_units) {
                        return {
                          name: unit?.selected_food?.name,
                          quantity: getQuantity(unit?.quantity, mealData),
                          workout_source: unit?.workout_source,
                        };
                      }
                      return null;
                    })
                    .filter(Boolean),
                };
              }),
            };
          } else {
            return {
              meals: mealData.map((meal) => {
                return {
                  ...meal,
                  meal_food_array: meal?.meal_food_array
                    ?.map((unit) => {
                      if (unit.measurement_units) {
                        return {
                          name: unit?.selected_food?.name,
                          // Saved for the calories of that day; rescaled to today's (as in the meal popup).
                          quantity: getQuantity(unit?.quantity, mealData),
                          workout_source: unit?.workout_source,
                        };
                      }
                      return null;
                    })
                    .filter(Boolean),
                };
              }),
            };
          }
        });
      }

      let combined_workouts = meals_array.flatMap((item) => item.meals);

      let allMeals = getValidMealArray(combined_workouts);

      if (allMeals && allMeals.length > 0) {
        let data = allMeals.map((meal) => {
          return {
            meal_food_array: meal?.meal_food_array,
          };
        });

        let update_array = consolidateMealFoods(data);

        if (update_array && update_array.length > 0) {
          let deficit_array = update_array.map((item) => {
            return {
              ...item,
              quantity: getQuantityPercentage(item?.quantity),
            };
          });
          setListData(deficit_array);
        }
      }

      if (result?.combined_workouts.length > 0) {
        enqueueSnackbar(
          "The shopping list for the selected dates has been successfully created.",
          { variant: "success" }
        );
      } else {
        enqueueSnackbar(
          "There are no food items to purchase for the selected dates.",
          { variant: "warning" }
        );
        setListData([]);
      }

      setIsLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  const downloadList = () => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Shopping List", 14, 15);

    doc.setFontSize(12);
    doc.text(
      `Name: ${user_info?.first_name + " " + user_info?.last_name}`,
      14,
      25
    );
    doc.text(`Email: ${user_info?.email}`, 14, 32);
    doc.addImage(logo_main, "PNG", 165, 10, 30, 25);

    const headers = [["Food Name", "Quantity"]];
    const data = listData.map((item) => [item.name, `${item.quantity} g`]);

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 40,
      headStyles: {
        fillColor: [4, 171, 187],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
    });

    doc.save("Food_List.pdf");
  };

  return (
    <GrPage>
      <GrPageHeader onBack={() => navigate(-1)} title="Shopping List" />

      {!user_info?.regular_day_calories ? (
        <GrNotice
          tone="warning"
          icon={<GrIcon icon={icons.alert} />}
          text={
            <>
              To use the shopping list feature, please{" "}
              <Box component={RouterLink} to={`/edit-profile`} sx={profileLinkSx}>
                Click here
              </Box>{" "}
              to complete your missing details (Gender, Weight, Height, Age, and Activity).
            </>
          }
        />
      ) : (
        <Box sx={layoutSx}>
          <GrCard>
            <Box sx={pickTopSx}>
              <GrText variant="secondary" sx={{ flex: "1 1 240px", minWidth: 0 }}>
                You can select one or multiple days for the shopping list by clicking the dates on
                the calendar.
              </GrText>
              {dates.length > 0 && (
                <GrButton loading={isLoading} disabled={isLoading} onClick={get_shoping_list}>
                  Create Shopping List
                </GrButton>
              )}
            </Box>
            <Box sx={calendarSx}>
              <Calendar
                value={dates}
                className="custom-calendar"
                onChange={setDates}
                multiple
                format="DD-MM-YYYY"
                weekNumbers
                plugins={[
                  <DatePanel
                    sort="date"
                    position="bottom"
                    className="custom-date-panel"
                  />,
                ]}
              />
            </Box>
          </GrCard>

          <GrCard>
            <GrCardHeader
              as="h2"
              title="Food Items List"
              action={
                listData?.length > 0 ? (
                  <GrButton
                    variant="secondary"
                    size="sm"
                    startIcon={<GrIcon icon={icons.download} size="sm" />}
                    onClick={downloadList}
                  >
                    Download PDF
                  </GrButton>
                ) : null
              }
              wrapAction
            />
            {listData && listData.length > 0 ? (
              <Box component="ul" sx={foodListSx}>
                {listData.map((item, index) => (
                  <GrListRow
                    key={index}
                    as="li"
                    title={`${index + 1}: ${item.name}`}
                    action={<GrBadge tone="brand">{item.quantity}g</GrBadge>}
                  />
                ))}
              </Box>
            ) : (
              <GrEmptyState compact icon={<GrIcon icon={icons.cart} />} title="Data Not Found" />
            )}
          </GrCard>
        </Box>
      )}
    </GrPage>
  );
};

export default ShoppingList;
